import { AxiosResponse } from 'axios';
import { Stream } from 'stream';
import { omit } from 'lodash';
import { resolveApiBase } from 'common/utils/resolve-api-base';
import { detectAppType } from 'common/utils/app-type';
import { ApiService } from '../../api.service';
import {
  ChangePipelineModelArgs,
  ContentGenerationPipeline,
  ContentGenerationPipelineAsset,
  GenerateKlingVideoArgs,
  GenerateVeoVideoArgs,
  GenerateFluxImageArgs,
  GetPipelinesArgs,
  PaginatedContentGenerationPipeline,
  PostPipelineArgs,
  SearchAssetsArgs,
  UdpateScriptArgs,
  GenerateOpenAIImageArgs,
  UploadAssetsArgs,
  UploadFinalVideoArgs,
  GetTenantAssetsParams,
  UploadTenantAssetsArgs,
  UploadSessionAssetsArgs,
  EnhancePromptArgs,
  EnhancePromptResponse,
  EditPipelineData,
  GetSceneAssetsArgs,
  GenerateThumbnailData,
  GenerateLumaVideoArgs,
  SaveSceneJsonBody,
  EnhanceJsonPromptBody,
  MessageForWeb,
  SendChatMessageArgs,
  PostPipelineStatsBody,
  PostPipelineStatsResponse,
  GenerateSeedreamsImageArgs,
  GenerateSeedanceVideoArgs,
  GetComparisonModelsResponse,
  GenerateSoraVideoArgs,
  PostModelVotesArgs,
  PostSharingRuleArgs,
  GetSharingRulesArgs,
  GetSharingRulesResponse,
  PatchSharingRuleArgs,
  GetSharedContentArgs,
  GetSharedContentResponse,
  CGPSharingRule,
} from './content-generation-pipelines.types';

class ContentGenerationPipelinesService extends ApiService {
  protected resolveApiPath(path: string): string {
    const appType = detectAppType();

    if (appType === 'supermodel') {
      // Add /supermodel prefix if not already present
      if (!path.startsWith('/supermodel')) {
        return `/supermodel${path}`;
      }
    }

    return `/vendors/target${path}`;
  }

  postPipeline = (body: PostPipelineArgs): Promise<AxiosResponse<{ pipelineID: string }>> => {
    return this.post('/content-generation-pipelines', body);
  };

  postFormDataPipeline = (body: PostPipelineArgs): Promise<AxiosResponse<{ pipelineID: string }>> => {
    const formData = new FormData();
    formData.append('prompt', body.prompt);
    formData.append('scopeID', body.scopeID.toString());
    formData.append('websearch', String(body.websearch));
    formData.append('type', body.type);
    formData.append('model', body.model);

    if (body.modelComparisonParams) {
      formData.append('modelComparisonParams', JSON.stringify(body.modelComparisonParams));
    }

    if (body.supermodelParams) {
      formData.append('supermodelParams', JSON.stringify(body.supermodelParams));
    }

    if (body.files && Array.isArray(body.files)) {
      body.files.forEach(file => {
        formData.append('files', file);
      });
    }

    return this.post('/content-generation-pipelines', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  getPipeline = (pipelineId: string): Promise<AxiosResponse<{ pipeline: ContentGenerationPipeline }>> => {
    return this.get(`/content-generation-pipelines/${pipelineId}`);
  };

  editPipeline = (data: EditPipelineData): Promise<AxiosResponse<EnhancePromptResponse>> => {
    const formData = new FormData();

    // Only append fields that are provided (not undefined or null)
    if (data.finalVideoTitle !== undefined && data.finalVideoTitle !== null) {
      formData.append('finalVideoTitle', data.finalVideoTitle.toString());
    }
    if (data.title !== undefined && data.title !== null) {
      formData.append('title', data.title.toString());
    }
    if (data.finalVideoDescription !== undefined && data.finalVideoDescription !== null) {
      formData.append('finalVideoDescription', data.finalVideoDescription.toString());
    }
    if (data.thumbnailFile !== undefined && data.thumbnailFile !== null) {
      formData.append('final_video_thumbnail', data.thumbnailFile);
    }
    if (data.finalVideoThumbnailURL !== undefined && data.finalVideoThumbnailURL !== null) {
      formData.append('finalVideoThumbnailURL', data.finalVideoThumbnailURL);
    }
    if (data.shotList !== undefined && data.shotList !== null) {
      formData.append('shotList', data.shotList.toString());
    }
    if (data.productionNotes !== undefined && data.productionNotes !== null) {
      formData.append('productionNotes', data.productionNotes.toString());
    }
    if (data.modelComparisonParams !== undefined && data.modelComparisonParams !== null) {
      formData.append('modelComparisonParams', JSON.stringify(data.modelComparisonParams));
    }

    return this.patch(`/content-generation-pipelines/${data?.pipelineId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  getPipelines = (
    args: GetPipelinesArgs
  ): Promise<AxiosResponse<{ items: PaginatedContentGenerationPipeline[]; totalItems: number }>> => {
    return this.get('/content-generation-pipelines', { params: args.query });
  };

  postScriptStream = (pipelineId: string): Promise<AxiosResponse<Stream>> => {
    return this.post(`/content-generation-pipelines/${pipelineId}/generate-script`);
  };

  changePipelineModel = (args: ChangePipelineModelArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/select-model`, args.body);
  };

  deletePipeline = (pipelineId: string): Promise<AxiosResponse> => {
    return this.delete(`/content-generation-pipelines/${pipelineId}`);
  };

  updateScript = (args: UdpateScriptArgs): Promise<AxiosResponse> => {
    return this.patch(`/content-generation-pipelines/${args.id}/script`, args.body);
  };

  searchAssets = (
    args: SearchAssetsArgs
  ): Promise<AxiosResponse<{ draftAssets: ContentGenerationPipelineAsset[] }>> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/search`, args.body);
  };

  saveDraft = (assetId: number): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/assets/${assetId}/save-draft`);
  };

  deleteAsset = (assetId: number): Promise<AxiosResponse> => {
    return this.delete(`/content-generation-pipelines/assets/${assetId}`);
  };

  generateKlingVideo = (args: GenerateKlingVideoArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/generate/kling/multi-image2video`, args.body);
  };

  generateLumaVideo = (args: GenerateLumaVideoArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/generate/luma-ai/reframe-video`, args.body);
  };

  generateVeoVideo = (args: GenerateVeoVideoArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/generate/veo`, args.body);
  };

  generateSeedanceVideo = (args: GenerateSeedanceVideoArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/generate/seedance/video`, args.body);
  };

  generateSoraVideo = (args: GenerateSoraVideoArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/generate/sora`, args.body);
  };

  generateFluxImage = (args: GenerateFluxImageArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/generate/flux/image`, args.body);
  };

  generateOpenAIImage = (args: GenerateOpenAIImageArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/generate/openai/image`, args.body);
  };

  generateSeedreamImage = (args: GenerateSeedreamsImageArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.id}/assets/generate/seedream/image`, args.body);
  };

  saveSceneJson = (body: SaveSceneJsonBody): Promise<AxiosResponse> => {
    return this.patch(`/content-generation-pipelines/scenes/${body?.sceneID}`, omit(body, 'sceneID'));
  };

  uploadAssets = (
    args: UploadAssetsArgs
  ): Promise<AxiosResponse<{ uploadedAssets: ContentGenerationPipelineAsset[] }>> => {
    const formData = new FormData();
    formData.append('sceneID', args.sceneID.toString());
    args.files.forEach(file => {
      formData.append('files', file);
    });

    return this.post(`/content-generation-pipelines/${args.pipelineId}/assets/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  uploadFinalVideo = (args: UploadFinalVideoArgs): Promise<AxiosResponse> => {
    if (args.file) {
      const formData = new FormData();
      formData.append('file', args.file);

      return this.post(`/content-generation-pipelines/${args.pipelineId}/assets/final-video/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }

    if (args.fileURL) {
      return this.post(
        `/content-generation-pipelines/${args.pipelineId}/assets/final-video/upload`,
        { fileURL: args.fileURL },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
    }

    throw new Error('Either file or fileURL must be provided');
  };

  getTenantAssets = (
    params: GetTenantAssetsParams
  ): Promise<AxiosResponse<{ assets: ContentGenerationPipelineAsset[] }>> => {
    return this.get(`/scopes/${params?.scopeID}/assets`, { params: omit(params, 'scopeID') });
  };

  uploadTenantAssets = (
    args: UploadTenantAssetsArgs
  ): Promise<AxiosResponse<{ uploadedAssets: ContentGenerationPipelineAsset[] }>> => {
    const formData = new FormData();
    formData.append('scopeID', args.scopeID.toString());
    args.files.forEach(file => {
      formData.append('files', file);
    });

    return this.post('/content-generation-pipelines/assets/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  uploadSessionAssets = (
    args: UploadSessionAssetsArgs
  ): Promise<AxiosResponse<{ uploadedAssets: ContentGenerationPipelineAsset[] }>> => {
    const formData = new FormData();
    formData.append('sessionID', args.sessionID.toString());
    args.files.forEach(file => {
      formData.append('files', file);
    });

    return this.post('/content-generation-pipelines/assets/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  enhancePrompt = (args: EnhancePromptArgs): Promise<AxiosResponse<EnhancePromptResponse>> => {
    return this.post('/content-generation-pipelines/enhance-prompt', args);
  };

  getPlaygroundPipeline = (scopeID: number): Promise<AxiosResponse<{ pipelineID: string }>> => {
    return this.get(`/content-generation-pipelines/playground?scopeID=${scopeID}`);
  };

  getSceneAssets = (
    args: GetSceneAssetsArgs
  ): Promise<AxiosResponse<{ totalCount: number; assets: ContentGenerationPipelineAsset[] }>> => {
    return this.get(`/content-generation-pipelines/scenes/${args.id}/assets`, { params: args.params });
  };

  generateOpenAIThumbnail = (data: GenerateThumbnailData): Promise<AxiosResponse<Stream>> => {
    return this.post(
      `/content-generation-pipelines/${data?.pipelineId}/assets/generate/openai/thumbnail`,
      omit(data, 'pipelineId')
    );
  };

  generateFluxThumbnail = (data: GenerateThumbnailData): Promise<AxiosResponse<Stream>> => {
    return this.post(
      `/content-generation-pipelines/${data?.pipelineId}/assets/generate/flux/thumbnail`,
      omit(data, 'pipelineId')
    );
  };

  generateScenes = (pipelineId: string): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${pipelineId}/generate-scenes`);
  };

  enhanceJsonPrompt = (body: EnhanceJsonPromptBody): Promise<AxiosResponse<{ enhancedPrompt: string }>> => {
    return this.post('/content-generation-pipelines/enhance-json-prompt', body);
  };

  startThumbnailSetJob = (pipelineId: string): Promise<AxiosResponse<{ id: string }>> => {
    return this.post(`/content-generation-pipelines/${pipelineId}/set-thumbnail`);
  };

  getChatMessages = (pipelineId: string): Promise<AxiosResponse<{ messages: MessageForWeb[] }>> => {
    return this.get(`/content-generation-pipelines/${pipelineId}/chat/messages`);
  };

  sendChatMessage = (args: SendChatMessageArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/${args.pipelineId}/chat/messages`, args.body);
  };

  sendChatMessageStream = (args: SendChatMessageArgs): Promise<Response> => {
    // Access baseURL through the instance since host is private
    const baseURL = resolveApiBase();
    const path = this.resolveApiPath(`/content-generation-pipelines/${args.pipelineId}/chat/stream`);
    const url = `${baseURL}${path}`;
    const token = localStorage.getItem('token');

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(args.body),
    });
  };

  postPipelineStats = (body: PostPipelineStatsBody): Promise<AxiosResponse<PostPipelineStatsResponse>> => {
    return this.post('/content-generation-pipelines/stats', body);
  };

  getComparisonModels = (): Promise<AxiosResponse<GetComparisonModelsResponse>> => {
    return this.get('/content-generation-pipelines/comparison-models');
  };

  postModelVotes = (args: PostModelVotesArgs): Promise<AxiosResponse> => {
    return this.post(`/content-generation-pipelines/v2/${args.pipelineId}/model-votes`, args.body);
  };

  // Sharing Rules APIs
  postSharingRule = (args: PostSharingRuleArgs): Promise<AxiosResponse<{ sharingRule: CGPSharingRule }>> => {
    return this.post('/sharing-rules', args);
  };

  getSharingRules = (args: GetSharingRulesArgs): Promise<AxiosResponse<GetSharingRulesResponse>> => {
    return this.get('/sharing-rules', { params: { pipelineID: args.pipelineID } });
  };

  patchSharingRule = (args: PatchSharingRuleArgs): Promise<AxiosResponse<{ sharingRule: CGPSharingRule }>> => {
    return this.patch(`/sharing-rules/${args.ruleID}`, { rule: args.rule });
  };

  deleteSharingRule = (ruleID: string): Promise<AxiosResponse> => {
    return this.delete(`/sharing-rules/${ruleID}`);
  };

  getSharedContent = (args: GetSharedContentArgs): Promise<AxiosResponse<GetSharedContentResponse>> => {
    return this.get(`/shared-content/${args.ruleID}`);
  };
}

export const contentGenerationPipelinesService = new ContentGenerationPipelinesService(resolveApiBase());
